import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../ui/PageHeader';
import { TabPanel, Tabs } from '../ui/Tabs';
import { LoadFailed } from '../ui/LoadState';
import { Skeleton } from '../../ui/Skeleton';
import { today } from '../../core/format/dates';
import { locationsDue } from '../../core/rules/stockCount';
import { stockValue } from '../../core/rules/stockValue';
import { money } from '../../core/format/currency';
import { useStock } from './useStock';
import type { StockItem } from '../../core/types/stock';
import { StockList } from './StockList';
import { StockCount } from './count/StockCount';
import { PurchaseReceipts } from './receipts/PurchaseReceipts';
import { ReorderList } from './ReorderList';
type TabKey = 'overzicht' | 'tellen' | 'bestellen' | 'inkoop';
const descriptions: Record<TabKey, string> = {
  overzicht: 'Webshop = wat de webshop verkoopt en verstuurt. Salon = wat de kassa verkoopt. Leeg (—) = niet bijgehouden.',
  tellen: 'Eén keer per week alles tellen, per plek. Verschillen worden met de reden "telling" geboekt.',
  bestellen: 'Wat je bij de leverancier bestelt, per merk. Pas de aantallen aan en kopieer de lijst in een mail.',
  inkoop: 'Leveringen boeken vanaf de inkoopfactuur, met de factuur erbij.'
};

/** Wat er nu op de plank ligt, tegen inkoopprijs: het getal dat de boekhouder op de balans zet. */
function StockValueLine({
  items
}: {
  items: readonly StockItem[];
}) {
  const v = stockValue(items);
  return <p className="mb-5 text-[13px] text-muted" data-id="element-4731" data-mp="1653uzu7jroo2">
      Voorraadwaarde tegen inkoopprijs (excl. btw): <span className="tabular-nums text-ink" data-id="element-4732" data-mp="1b4701enj1uw1">{money(v.totalCents)}</span>
      <span className="tabular-nums" data-id="element-4733" data-mp="1f73csoqdyws2">
        {' '}
        · webshop {money(v.byLocation.webshop)} · salon {money(v.byLocation.salon)}
      </span>
      {v.withoutCost > 0 && <span className="text-clay" data-id="element-4734" data-mp="nhi9y5od6w4c">
          {' '}
          · {v.withoutCost} {v.withoutCost === 1 ? 'artikel' : 'artikelen'} zonder inkoopprijs telt niet mee
        </span>}
    </p>;
}

/**
 * Aanbod › Voorraad, in vier tabbladen: Overzicht (stand en acties per artikel), Tellen (de
 * wekelijkse telling), Bestellijst (wat er besteld moet worden) en Inkoop (inkoopfacturen).
 * Eén keer laden, gedeeld door alle vier.
 */
export function Stock() {
  const stock = useStock();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: TabKey = raw === 'tellen' || raw === 'bestellen' || raw === 'inkoop' ? raw : 'overzicht';
  const countsDue = locationsDue(stock.items ?? [], stock.lastCountAt, today()).length;
  const tabs: {
    key: TabKey;
    label: string;
    count?: number;
  }[] = [{
    key: 'overzicht',
    label: 'Overzicht'
  }, {
    key: 'tellen',
    label: 'Tellen',
    ...(stock.items && countsDue > 0 ? {
      count: countsDue
    } : {})
  }, {
    key: 'bestellen',
    label: 'Bestellijst'
  }, {
    key: 'inkoop',
    label: 'Inkoop'
  }];
  const setTab = (next: TabKey) => setParams(prev => {
    const p = new URLSearchParams(prev);
    if (next === 'overzicht') p.delete('tab');else p.set('tab', next);
    p.delete('filter');
    return p;
  }, {
    replace: true
  });
  return <div data-id="element-4735" data-mp="34ov1qedbh2md">
      <PageHeader title="Voorraad" description={descriptions[tab]} data-id="element-4736" data-mp="2ptymvib05h0b" />
      <Tabs id="voorraad" label="Voorraad" tabs={tabs} value={tab} onChange={setTab} className="mb-5" data-id="element-4737" data-mp="2pvrup0xcq79w" />
      {tab === 'overzicht' && stock.items && <StockValueLine items={stock.items} data-id="element-4738" data-mp="3ii2s62pnvs2h" />}

      <TabPanel id="voorraad" value={tab} data-id="element-4739" data-mp="3ije6uxknak8d">
        {stock.status === 'error' && !stock.items ? <LoadFailed title="De voorraad laden lukte niet" onRetry={stock.reload} data-id="element-4740" data-mp="3ngxkbv9iqsz9" /> : !stock.items ? <div role="status" aria-label="Voorraad laden" className="space-y-2" data-id="element-4741" data-mp="38qqa22to0g9n">
            <Skeleton className="h-14 w-full rounded-lg" data-id="element-4742" data-mp="3dqbayiseayp0" />
            <Skeleton className="h-14 w-full rounded-lg" data-id="element-4743" data-mp="5bkj057wzdkp" />
            <Skeleton className="h-14 w-full rounded-lg" data-id="element-4744" data-mp="59zpmixc60of" />
          </div> : tab === 'tellen' ? <StockCount stock={stock} data-id="element-4745" data-mp="a4nhmrrjtd7r" /> : tab === 'bestellen' ? <ReorderList stock={stock} data-id="element-4746" data-mp="2il6mu2rcgv32" /> : tab === 'inkoop' ? <PurchaseReceipts stock={stock} data-id="element-4747" data-mp="2djr0m7dfkij3" /> : <StockList stock={stock} data-id="element-4748" data-mp="28lpjhrzketa9" />}
      </TabPanel>
    </div>;
}